/**
 * t428u: the device permissions the Hub asks for (iPhone app, phone web and desktop web), the first sign-in
 * "Set up your phone" screen and the reminder that comes back while something's still off. Pure, so the iPhone app
 * (apps/mobile/src/permissions) and the web (apps/web/src/lib/devicePermissions.ts) share the words and the rules.
 *
 * - Notifications: every push (messages, shifts, approvals) and incoming calls (on the iPhone the call screen rides
 *   on the same permission: PushKit / CallKit need nothing more from the person).
 * - Microphone: calls and ad-hoc meeting rooms (LiveKit). Only while the Calls module is on.
 * - Camera: video calls, scanning QR points, photos for chats, tasks, issues and vehicle checks.
 * - Location: only for people whose clock-in checks the building (a geofence) or who drive runs.
 * Photos and files need no permission (the system pickers), and Face ID is asked when the app lock is turned on.
 */

import { isModuleOn, type ModuleId } from '../modules';
import type { Geofence } from '../workforce/clock';

export type DevicePermission = 'notifications' | 'microphone' | 'camera' | 'location';
/**
 * granted: on. ask: not asked yet (Allow shows the system prompt). denied: said no (the iPhone only asks once, so
 * it's Settings from here). unknown: the browser can't say up front (asked when needed). unsupported: not on this device.
 */
export type DevicePermissionStatus = 'granted' | 'ask' | 'denied' | 'unknown' | 'unsupported';

export interface DevicePermissionRow { kind: DevicePermission; title: string; why: string }

export interface DevicePermissionNeeds {
  /** The Calls module is on for this workspace (microphone; the camera line mentions video calls). */
  calls: boolean;
  /** Clock-in checks the building, or the person drives runs. */
  location: boolean;
}

/** What this person's workspace uses: calls unless switched off; location when clock-in checks the building or they drive. */
export function permissionNeeds(o: { modulesOff?: readonly ModuleId[] | null; geofence?: Pick<Geofence, 'mode'> | null; drives?: boolean }): DevicePermissionNeeds {
  return { calls: isModuleOn(o.modulesOff ?? undefined, 'calls'), location: Boolean(o.geofence && o.geofence.mode !== 'off') || Boolean(o.drives) };
}

/** The rows to show, most important first. */
export function devicePermissionRows(needs: DevicePermissionNeeds): DevicePermissionRow[] {
  const rows: DevicePermissionRow[] = [
    { kind: 'notifications', title: 'Notifications', why: needs.calls ? 'Messages, shift changes and calls, even when the app is closed.' : 'Messages and shift changes, even when the app is closed.' },
  ];
  if (needs.calls) rows.push({ kind: 'microphone', title: 'Microphone', why: 'So people can hear you on calls.' });
  rows.push({ kind: 'camera', title: 'Camera', why: needs.calls ? 'Video calls, scanning QR codes and taking photos.' : 'Scanning QR codes and taking photos.' });
  if (needs.location) rows.push({ kind: 'location', title: 'Location', why: 'Checks you’re at work when you clock in.' });
  return rows;
}

/** Still to do: not asked yet, or turned off. Unknown and unsupported never count (nothing the person can fix here). */
export const isMissing = (s: DevicePermissionStatus | undefined) => s === 'ask' || s === 'denied';

export function missingPermissions(rows: DevicePermissionRow[], status: Partial<Record<DevicePermission, DevicePermissionStatus>>): DevicePermission[] {
  return rows.filter(r => isMissing(status[r.kind])).map(r => r.kind);
}

/** The first sign-in screen shows once per device, and only when something is still to allow. */
export function shouldShowSetup(done: boolean, missing: DevicePermission[]): boolean {
  return !done && missing.length > 0;
}

/** The reminder's memory on this device. */
export interface PermissionReminderState { dismissed: number; last: number | null }
export const EMPTY_REMINDER: PermissionReminderState = { dismissed: 0, last: null };
/** After a dismissal it comes back no sooner than this. */
export const REMINDER_GAP_MS = 3 * 24 * 60 * 60 * 1000;
/** Dismissed this many times: it stops (the Phone permissions row in Me stays). */
export const REMINDER_MAX_DISMISSALS = 5;

export function parseReminderState(raw: string | null | undefined): PermissionReminderState {
  try {
    const v = JSON.parse(raw ?? 'null') as Partial<PermissionReminderState> | null;
    const dismissed = typeof v?.dismissed === 'number' && v.dismissed >= 0 ? Math.floor(v.dismissed) : 0;
    const last = typeof v?.last === 'number' && Number.isFinite(v.last) ? v.last : null;
    return { dismissed, last };
  } catch { return EMPTY_REMINDER; }
}

export function shouldShowReminder(s: PermissionReminderState, missing: DevicePermission[], now: number, setupDone = true): boolean {
  // Not on top of the first sign-in screen, and never when everything's on.
  if (!setupDone || !missing.length) return false;
  if (s.dismissed >= REMINDER_MAX_DISMISSALS) return false;
  return s.last === null || now - s.last >= REMINDER_GAP_MS;
}

/** "Not now" / "Continue" on the first sign-in screen: the reminder waits the same 3 days (not counted as a dismissal). */
export function afterSetup(s: PermissionReminderState, now: number): PermissionReminderState {
  return { dismissed: s.dismissed, last: s.last ?? now };
}

export function dismissReminder(s: PermissionReminderState, now: number): PermissionReminderState {
  return { dismissed: s.dismissed + 1, last: now };
}

/** One line; notifications come first because they matter most. */
export function reminderText(missing: DevicePermission[]): string {
  if (missing.includes('notifications')) return 'Turn on notifications so you don’t miss messages, shifts and calls.';
  const words = missing.map(k => (k === 'microphone' ? 'microphone' : k === 'camera' ? 'camera' : 'location'));
  const list = words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words[0];
  return `Allow ${list} so everything works on this device.`;
}
